import { useEffect, useMemo, useRef, useState } from 'react';
import { useViewport } from 'hooks/3catinfo/useViewport';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
import __isEmpty from 'lodash/isEmpty';
import __merge from 'lodash/merge';
import clsx from 'clsx';
import styles from './graficbarres.module.scss';

ChartJS.register(Tooltip, Legend, BarElement, CategoryScale, LinearScale);

Tooltip.positioners.bottomOffset = function (items, eventPosition) {
    if (!items.length) return false;

    const item = items[0];
    const element = item.element;

    const basePosition = Tooltip.positioners.nearest(items, eventPosition);

    const barHeight = Math.abs(element.y - element.base);
    const offset = Math.max(2, Math.min(32, barHeight * 0.25));

    return {
        x: basePosition.x,
        y: basePosition.y + offset
    };
};

const blendWithWhite = (rgbString, alpha = 0.4) => {
    if (!rgbString || typeof rgbString !== 'string') {
        return 'rgb(220,220,220)';
    }
    const match = rgbString.match(/rgb\s*\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)/);
    if (!match) return rgbString;

    const r = parseInt(match[1], 10);
    const g = parseInt(match[2], 10);
    const b = parseInt(match[3], 10);

    const blendedR = Math.round((1 - alpha) * 255 + alpha * r);
    const blendedG = Math.round((1 - alpha) * 255 + alpha * g);
    const blendedB = Math.round((1 - alpha) * 255 + alpha * b);

    return `rgb(${blendedR}, ${blendedG}, ${blendedB})`;
};

const patternCache = new Map();
const createDiagonalPattern = (bgColor, blendWhite = false, lnColor = '#111617') => {
    const key = `${bgColor}-${blendWhite}-${lnColor}`;
    if (patternCache.has(key)) return patternCache.get(key);

    const size = 24;
    const spacing = 8;
    const thickness = 1;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    const finalBg = blendWhite ? blendWithWhite(bgColor) : bgColor;
    ctx.fillStyle = finalBg;
    ctx.fillRect(0, 0, size, size);
    ctx.strokeStyle = lnColor;
    ctx.lineWidth = thickness;
    ctx.lineCap = 'square';

    for (let i = -size; i < size * 2; i += spacing) {
        ctx.beginPath();
        ctx.moveTo(i, size);
        ctx.lineTo(i + size, 0);
        ctx.stroke();
    }

    const pattern = ctx.createPattern(canvas, 'repeat');
    patternCache.set(key, pattern);

    return pattern;
};

const getDataSet = ({
    data,
    dataString,
    labels,
    colors,
    barSize,
    borderRadius,
    mostrarLlegendaX,
    isSecondary = false,
    showSecondaryGraph = false,
    isEmptyDataMain = false,
    datalabelsLlargs,
    isOnlyMobile,
    isSmallMobile
}) => {
    const backgroundColor = isSecondary ? colors.map((color) => createDiagonalPattern(color)) : colors;
    const displayDataLabels = isSecondary ? mostrarLlegendaX && isEmptyDataMain : mostrarLlegendaX && !showSecondaryGraph;
    return {
        data,
        dataString,
        datalabelsArray: labels,
        backgroundColor,
        hoverOffset: 4,
        borderColor: isSecondary ? 'rgba(0,0,0,0)' : 'transparent',
        borderWidth: 2,
        barThickness: isSecondary || showSecondaryGraph ? barSize / 2 : barSize,
        borderRadius: {
            topLeft: borderRadius,
            topRight: borderRadius,
            bottomLeft: 0,
            bottomRight: 0
        },
        categoryPercentage: 1.0,
        barPercentage: 1.0,
        datalabels: {
            display: displayDataLabels,
            anchor: 'end',
            align: 'start',
            offset: isSmallMobile && datalabelsLlargs ? -45 : -18,
            color: '#111617',
            rotation: isSmallMobile && datalabelsLlargs ? -90 : 0,
            font: {
                size: isOnlyMobile && datalabelsLlargs ? 11 : 12,
                family: "'Bw 3cat', Helvetica, Arial, sans-serif"
            },
            formatter: (_, context) => dataString[context.dataIndex]
        },
        ...(isSecondary && { isPattern: true })
    };
};

const GraficBarres = (props) => {
    const { dataMain, dataSecondary, className, numColumnsRow, optionsChart, paramsChart } = props;
    const params = useMemo(() => {
        const paramsDefault = {
            barSize: 36,
            showSecondaryGraph: false,
            borderRadius: 4,
            mostrarLlegendaX: false,
            drawLineLabelX: false,
            font: "'Bw 3cat', Helvetica, Arial, sans-serif",
            datalabelsLlargs: false
        };
        return __merge({}, paramsDefault, paramsChart);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paramsChart]);

    const isEmptyDataMain = __isEmpty(dataMain?.labels);

    const { isOnlyMobile, isSmallMobile, viewport } = useViewport();

    const graphRef = useRef();
    const originalMainColors = useRef([]);
    const originalSecondaryColors = useRef([]);

    useEffect(() => {
        originalMainColors.current = [...dataMain.colors];
        originalSecondaryColors.current = [...dataSecondary.colors];
    }, [dataMain.colors, dataSecondary.colors]);

    const stylesGrafic = clsx(
        styles.grafic,
        params?.isGraficGran && styles.gran,
        !params?.isGraficGran && styles.petit,
        styles.peu,
        numColumnsRow > 0 && styles['col' + numColumnsRow],
        !__isEmpty(className) && className
    );

    const resetBars = (chart) => {
        if (!chart) return;
        chart.data.datasets.forEach((ds) => {
            if (ds.isPattern) {
                ds.backgroundColor = ds.data.map((_, i) => createDiagonalPattern(originalSecondaryColors.current[i]));
            } else {
                ds.backgroundColor = [...originalMainColors.current];
            }
        });
        chart.setActiveElements([]);
        chart.tooltip.setActiveElements([], { x: 0, y: 0 });
        chart.update({
            duration: 250,
            easing: 'easeOutQuad'
        });
    };

    const applySelection = (chart, selectedDatasetIndex, selectedIndex) => {
        if (!chart) return;
        chart.data.datasets.forEach((ds, dsIndex) => {
            if (ds.isPattern) {
                ds.backgroundColor = ds.data.map((_, i) => {
                    const baseColor = originalSecondaryColors.current[i];
                    if (dsIndex === selectedDatasetIndex && i === selectedIndex) {
                        return createDiagonalPattern(baseColor);
                    }
                    return createDiagonalPattern(baseColor, true);
                });
            } else {
                ds.backgroundColor = ds.data.map((_, i) => {
                    const baseColor = originalMainColors.current[i];
                    if (dsIndex === selectedDatasetIndex && i === selectedIndex) {
                        return baseColor;
                    }
                    return blendWithWhite(baseColor);
                });
            }
        });
        chart.update({
            duration: 300,
            easing: 'easeOutCubic'
        });
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            const chart = graphRef.current;
            if (!chart) return;
            const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
            if (!elements.length) {
                resetBars(chart);
            }
        };
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);
        document.addEventListener('pointerdown', handleClickOutside);
        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
            document.removeEventListener('pointerdown', handleClickOutside);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const chart = graphRef.current;
        if (!chart) return;
        const canvas = chart.canvas;
        const handleMouseLeave = () => {
            resetBars(chart);
        };
        canvas.addEventListener('mouseleave', handleMouseLeave);
        return () => {
            canvas.removeEventListener('mouseleave', handleMouseLeave);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const centeredSingleBarPlugin = {
        id: 'centeredSingleBarPlugin',
        beforeDatasetsDraw(chart) {
            const datasets = chart.data.datasets;
            if (!params?.showSecondaryGraph || __isEmpty(dataMain?.labels) || __isEmpty(dataSecondary?.labels)) return;
            datasets.forEach((dataset, datasetIndex) => {
                const meta = chart.getDatasetMeta(datasetIndex);
                meta.data.forEach((bar, i) => {
                    const value = dataset.data[i];
                    if (!value || value === '0') return;
                    const otherDatasetIndex = datasetIndex === 0 ? 1 : 0;
                    const otherDataset = datasets[otherDatasetIndex];
                    const otherValue = otherDataset?.data[i];
                    if (!otherValue || otherValue === '0') {
                        if (bar.$baseX === undefined) bar.$baseX = bar.x;
                        const barWidth = bar.width ?? 0;
                        bar.x = bar.$baseX + (datasetIndex === 0 ? barWidth / 2 : -barWidth / 2);
                    } else {
                        if (bar.$baseX !== undefined) bar.x = bar.$baseX;
                    }
                });
            });
        }
    };

    let barChartSize = params?.barSize;
    const barPlugin = {
        beforeUpdate(chart) {
            const chartWidth = chart.width;
            const minBarSpacing = 12;
            const minBarSpacingSlim = params?.showSecondaryGraph ? 10 : minBarSpacing;
            if (
                (!params?.showSecondaryGraph || __isEmpty(dataSecondary) || __isEmpty(dataMain)) &&
                chartWidth < (barChartSize + minBarSpacing) * dataMain.dataNum.length - minBarSpacing
            ) {
                barChartSize = (chartWidth - minBarSpacing * (dataMain.dataNum.length - 1)) / dataMain.dataNum.length;
            }
            const datasets = chart.data.datasets;
            const labelsCount = chart.data.labels.length;
            if (!labelsCount) return;
            const totalBars = datasets.reduce((count, dataset, i) => {
                const meta = chart.getDatasetMeta(i);
                if (meta.hidden) return count;

                return count + dataset.data.filter((v) => v !== null && v !== undefined && v !== '-').length;
            }, 0);
            if (!totalBars) return;
            const groupWidth = (chartWidth - labelsCount * minBarSpacingSlim) / totalBars;
            const computedBarSize =
                datasets.length > 1 && !isEmptyDataMain ? Math.min(groupWidth, barChartSize / 2) : barChartSize;
            datasets.forEach((dataset) => {
                dataset.barThickness = computedBarSize;
            });
        }
    };
    const verticalLabelLinePlugin = {
        id: 'verticalLabelLinePlugin',
        beforeDraw(chart) {
            if (params?.drawLineLabelX) {
                const { ctx, chartArea, scales } = chart;
                const xScale = scales.x;
                if (!xScale) return;
                const yStart = chartArea.bottom;
                const yEnd = yStart + 15;
                ctx.save();
                ctx.strokeStyle = '#DCE3E5';
                ctx.lineWidth = 1;
                xScale.ticks.forEach((_, index) => {
                    const x = xScale.getPixelForTick(index);
                    ctx.beginPath();
                    ctx.moveTo(x, yStart);
                    ctx.lineTo(x, yEnd);
                    ctx.stroke();
                });
                ctx.restore();
            }
        }
    };

    let labelsX = dataMain.labels;
    let mainDataNum = dataMain.dataNum;
    let mainDataString = dataMain.dataString;
    let mainColors = dataMain.colors;
    let secondaryDataNum = dataSecondary.dataNum;
    let secondaryDataString = dataSecondary.dataString;
    let secondaryColors = dataSecondary.colors;
    if (params?.showSecondaryGraph) {
        const unifiedLabels = Array.from(new Set([...dataMain.labels, ...dataSecondary.labels]));
        const mapMain = new Map(dataMain.labels.map((label, i) => [label, i]));
        const mapSecondary = new Map(dataSecondary.labels.map((label, i) => [label, i]));
        mainDataNum = unifiedLabels.map((label) => (mapMain.has(label) ? dataMain.dataNum[mapMain.get(label)] : null));
        mainDataString = unifiedLabels.map((label) => (mapMain.has(label) ? dataMain.dataString[mapMain.get(label)] : '0'));
        mainColors = unifiedLabels.map((label) =>
            mapMain.has(label) ? dataMain.colors[mapMain.get(label)] : dataSecondary.colors[mapSecondary.get(label)]
        );
        secondaryDataNum = unifiedLabels.map((label) =>
            mapSecondary.has(label) ? dataSecondary.dataNum[mapSecondary.get(label)] : null
        );
        secondaryDataString = unifiedLabels.map((label) =>
            mapSecondary.has(label) ? dataSecondary.dataString[mapSecondary.get(label)] : '0'
        );
        secondaryColors = unifiedLabels.map((label) =>
            mapSecondary.has(label) ? dataSecondary.colors[mapSecondary.get(label)] : dataMain.colors[mapMain.get(label)]
        );
        labelsX = unifiedLabels;
    }

    const baseDataset = getDataSet({
        data: mainDataNum,
        dataString: mainDataString,
        labels: labelsX,
        colors: mainColors,
        barSize: barChartSize,
        borderRadius: params?.borderRadius,
        mostrarLlegendaX: params?.mostrarLlegendaX,
        showSecondaryGraph: params?.showSecondaryGraph,
        datalabelsLlargs: params?.datalabelsLlargs,
        isSecondary: false,
        isOnlyMobile,
        isSmallMobile
    });

    const datasets = !params?.showSecondaryGraph
        ? [{ ...baseDataset, isPattern: false }]
        : [
              ...(!isEmptyDataMain ? [{ ...baseDataset, isPattern: false }] : []),
              getDataSet({
                  data: secondaryDataNum,
                  dataString: secondaryDataString,
                  labels: labelsX,
                  colors: secondaryColors,
                  barSize: barChartSize,
                  borderRadius: params?.borderRadius,
                  mostrarLlegendaX: params?.mostrarLlegendaX,
                  showSecondaryGraph: params?.showSecondaryGraph,
                  datalabelsLlargs: params?.datalabelsLlargs,
                  isSecondary: true,
                  isEmptyDataMain,
                  isOnlyMobile,
                  isSmallMobile
              })
          ];

    const dataGraph = {
        labels: labelsX,
        datasets
    };

    const baseOptions = useMemo(
        () => ({
            indexAxis: 'x',
            responsive: true,
            maintainAspectRatio: false,
            layout: {
                padding: {
                    left: !params?.mostrarLlegendaX
                        ? 0
                        : (() => {
                              const ctx = document.createElement('canvas').getContext('2d');
                              ctx.font = `${isOnlyMobile ? 10 : 12}px ${params?.font}`;
                              return Math.max(0, ...labelsX.slice(0, 2).map((label) => ctx.measureText(label).width * 0.1));
                          })(),
                    right: 0,
                    bottom: params?.mostrarLlegendaX && !params?.showSecondaryGraph ? 20 : 0,
                    top: 25
                },
                top: 0
            },
            plugins: {
                tooltip: {
                    backgroundColor: '#21292C',
                    titleColor: '#FFF',
                    bodyColor: '#FFF',
                    borderWidth: 0,
                    position: 'bottomOffset',
                    padding: 10,
                    cornerRadius: 8,
                    usePointStyle: true,
                    boxWidth: 8,
                    boxHeight: 8,
                    footerMarginTop: 10,
                    footerSpacing: 6,
                    titleFont: {
                        family: params?.font,
                        size: 16,
                        style: 'normal',
                        weight: 'normal'
                    },
                    bodyFont: {
                        family: params?.font,
                        size: 16,
                        style: 'normal',
                        weight: 'normal'
                    },
                    footerFont: {
                        family: params?.font,
                        size: 12,
                        style: 'normal',
                        weight: '500'
                    },
                    callbacks: {
                        title: () => '',
                        label: (context) => {
                            const { dataset, dataIndex } = context;
                            return ` ${dataset.datalabelsArray[dataIndex]}`;
                        },
                        footer: (context) => {
                            const { dataset, dataIndex } = context[0];
                            return ` ${dataset.data[dataIndex]}`;
                        }
                    }
                },
                legend: {
                    display: false
                }
            },
            scales: {
                x: {
                    border: {
                        display: false
                    },
                    stacked: false,
                    ticks: {
                        display: true,
                        font: {
                            size: isOnlyMobile ? 10 : 12,
                            family: params?.font
                        },
                        maxRotation: 45,
                        minRotation: 45,
                        autoSkip: false,
                        padding: 8,
                        callback: function (value) {
                            if (params?.mostrarLlegendaX) {
                                const label = this.getLabelForValue(value);
                                return label;
                            }
                        }
                    },
                    grid: {
                        display: false,
                        drawBorder: false
                    }
                },
                y: {
                    display: false,
                    beginAtZero: true,
                    stacked: false,
                    border: {
                        display: false
                    },
                    ticks: {
                        display: false
                    },
                    grid: {
                        display: false
                    },
                    min: 0,
                    suggestedMax: params?.showSecondaryGraph
                        ? Math.max(dataMain.maxValue, dataSecondary.maxValue) * 1.12
                        : dataMain.maxValue * 1.12
                }
            },
            onClick: (event, elements) => {
                event.native?.stopPropagation?.();
                const chart = graphRef.current;
                if (!chart) return;
                if (!elements.length) {
                    resetBars(chart);
                    return;
                }
                const { datasetIndex, index } = elements[0];
                applySelection(chart, datasetIndex, index);
            },
            onHover: (event, elements) => {
                const chart = graphRef.current;
                if (!chart) return;
                if (!elements.length) {
                    resetBars(chart);
                    return;
                }
                const { datasetIndex, index } = elements[0];
                applySelection(chart, datasetIndex, index);
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }),
        [dataMain, dataSecondary, params, isOnlyMobile, labelsX]
    );

    const options = useMemo(() => {
        return __merge({}, baseOptions, optionsChart);
    }, [baseOptions, optionsChart]);

    return (
        <div className={styles.content}>
            <div className={styles.grafics}>
                <div className={stylesGrafic}>
                    <Bar
                        data={dataGraph}
                        options={options}
                        plugins={[barPlugin, verticalLabelLinePlugin, centeredSingleBarPlugin, ChartDataLabels]}
                        ref={graphRef}
                    />
                </div>
            </div>
        </div>
    );
};

export default GraficBarres;
